import { useEffect, useMemo, useRef } from 'react'
import type { TimelineBar, Timeline as TimelineData } from '@/api/schemas'
import { dateLocale, t } from '@/lib/i18n'
import { cn } from '@/lib/utils'

interface Props {
  timeline: TimelineData
  closeVotesManualThreshold: number
  onClosePhase: (phaseId: number) => void
  closing: Set<number>
  /** WEB-021: when a past day is selected, the Gantt cursor follows the
   *  picker instead of "today". Both are YYYY-MM-DD in the site's tz. */
  selectedDate?: string
  today?: string
}

/** Replicates the server's `_compute_timeline_layout` axis math (4 Oct
 *  2026): the axis spans all planned bars AND today, widened to the Monday
 *  of the first week and the Sunday of the last week. Returns the percent
 *  position of `ymd`, or null when it falls outside that axis. */
function computeCursorPct(bars: TimelineBar[], ymd: string, today?: string): number | null {
  if (bars.length === 0) return null
  const DAY = 24 * 60 * 60 * 1000
  const starts = bars.map((b) => Date.parse(`${b.planned_start}T00:00:00Z`))
  const ends = bars.map((b) => Date.parse(`${b.planned_end}T00:00:00Z`))
  const todayMs = today ? Date.parse(`${today}T00:00:00Z`) : NaN
  let tStart = Math.min(...starts, ...(Number.isNaN(todayMs) ? [] : [todayMs]))
  let tEnd = Math.max(...ends, ...(Number.isNaN(todayMs) ? [] : [todayMs]))
  const mondayOffset = (new Date(tStart).getUTCDay() + 6) % 7
  tStart -= mondayOffset * DAY
  const sundayOffset = 6 - ((new Date(tEnd).getUTCDay() + 6) % 7)
  tEnd += sundayOffset * DAY
  const totalMs = tEnd - tStart + DAY // end dates are inclusive (matches the server)
  const target = Date.parse(`${ymd}T00:00:00Z`)
  if (Number.isNaN(target) || target < tStart || target > tEnd) return null
  return Math.round(((target - tStart) / totalMs) * 1000) / 10
}

/** "5 Oct – 11 Oct", or just "11 Jan" for a one-day phase. */
function dateRange(start: string, end: string): string {
  return start === end ? shortDate(start) : `${shortDate(start)} – ${shortDate(end)}`
}

/** "5 Oct" from "2026-10-05". */
function shortDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return ymd
  return d.toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

export function Timeline({
  timeline,
  closeVotesManualThreshold,
  onClosePhase,
  closing,
  selectedDate,
  today,
}: Props) {
  // Cursor follows the date selector when set; falls back to the server's
  // `today_pct` (real today) for the default view. Computed before the
  // empty-bars guard so the hook order stays stable.
  const isPastView = Boolean(selectedDate && today && selectedDate !== today)
  const cursorPct = useMemo(() => {
    if (isPastView && selectedDate) return computeCursorPct(timeline.bars, selectedDate, today)
    return timeline.today_pct
  }, [isPastView, selectedDate, today, timeline.bars, timeline.today_pct])
  const cursorLabel = isPastView && selectedDate ? selectedDate.slice(5) : t('Today')

  if (timeline.bars.length === 0) return null


  return (
    <div id="project-timeline" className="mb-8 bg-card rounded-xl border border-border p-4 scroll-mt-4">
      {/* One Gantt for every screen size (7 Oct 2026: on phones it used to
          collapse into a one-line summary that clients missed). On a phone
          the name column narrows and only month labels show. */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-muted text-foreground-muted">
            {t('Project timeline')}
          </span>
        </div>
        <DesktopGantt
          timeline={timeline}
          closeVotesManualThreshold={closeVotesManualThreshold}
          onClosePhase={onClosePhase}
          closing={closing}
          cursorPct={cursorPct}
          cursorLabel={cursorLabel}
        />
      </div>
    </div>
  )
}

// ── Desktop (Gantt) ─────────────────────────────────────────────
//
// 4 Oct 2026 (Dori: "reset the timeline and do it again, cleaner"):
// rebuilt from scratch. The old table was wider than the card and
// auto-scrolled sideways, so the first and last phases were hidden; the
// dates sat beside the bars and ran into the axis labels; and the actual
// bar was a dark block on top of the planned one. Now:
//   - the whole project fits the card width (no sideways scroll);
//   - each phase's planned dates sit under its name, never on the chart;
//   - one bar per phase, coloured by status (done / in progress / next);
//   - actual progress is a thin line under the bar;
//   - axis labels live in their own header row; a legend explains it all.


/** Keep a bar inside the chart: a short bar at the very end gets a
 *  minimum width from the server and would spill past the right edge
 *  (4 Oct 2026, Mar 17 Handover on the last day). Returns left/width %. */
function clampBar(left: number, width: number): { left: number; width: number } {
  const w = Math.min(Math.max(width, 0), 100)
  return { left: Math.min(Math.max(left, 0), 100 - w), width: w }
}
const ROW_H = 44

type PhaseStatus = 'done' | 'active' | 'next'

function phaseStatus(bar: TimelineBar): PhaseStatus {
  if (bar.actual_end) return 'done'
  if (bar.actual_start) return 'active'
  return 'next'
}

const BAR_CLASS: Record<PhaseStatus, string> = {
  done: 'bg-positive/20 border-positive/50',
  active: 'bg-primary/15 border-primary/60',
  next: 'bg-muted border-border',
}
const LINE_CLASS: Record<PhaseStatus, string> = {
  done: 'bg-positive',
  active: 'bg-primary',
  next: 'bg-transparent',
}
const DOT_CLASS: Record<PhaseStatus, string> = {
  done: 'bg-positive',
  active: 'bg-primary',
  next: 'bg-input',
}
const STATUS_LABEL: Record<PhaseStatus, string> = {
  done: 'Done',
  active: 'In progress',
  next: 'Planned',
}

function DesktopGantt({
  timeline,
  closeVotesManualThreshold,
  onClosePhase,
  closing,
  cursorPct,
  cursorLabel,
}: Props & { cursorPct: number | null; cursorLabel: string }) {
  // Thin out axis labels when there are many ticks, but always keep the
  // first tick of each month (major).
  const ticks = timeline.months
  // A week label is skipped when it would touch its neighbour (month
  // labels always show).
  const MIN_GAP = 8
  const shown = new Set<number>()
  let lastPct = -100
  ticks.forEach((m, i) => {
    const nextMajor = ticks.slice(i + 1).find((t) => t.major)
    if (m.major || (m.pct - lastPct >= MIN_GAP && (!nextMajor || nextMajor.pct - m.pct >= MIN_GAP))) {
      shown.add(i)
      lastPct = m.pct
    }
  })

  // 7 Oct 2026 (Dori's pick): on a phone the full chart scrolls sideways
  // under a fixed name column, and opens scrolled to today.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || cursorPct === null || el.scrollWidth <= el.clientWidth) return
    const nameW = (el.querySelector('[data-name-col]') as HTMLElement | null)?.offsetWidth ?? 0
    const chartW = el.scrollWidth - nameW
    const visible = el.clientWidth - nameW
    el.scrollLeft = Math.max(0, (cursorPct / 100) * chartW - visible * 0.2)
  }, [cursorPct])

  const gridLines = (
    <>
      {ticks.map((m, i) => (
        <div
          key={`g-${m.label}-${i}`}
          className={cn('absolute top-0 bottom-0 border-l', m.major ? 'border-border/50' : 'border-border-subtle/50')}
          style={{ left: `${m.pct}%` }}
        />
      ))}
    </>
  )

  return (
    <div>
      <p className="sm:hidden mb-2 text-[11px] text-muted-foreground">{t('Swipe the chart sideways to see the whole project →')}</p>
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-start sm:justify-end gap-x-3 gap-y-1 sm:gap-x-4 mb-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-2.5 rounded-sm bg-muted border border-border" /> {t('Planned')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-2.5 rounded-sm bg-primary/15 border border-primary/60" /> {t('In progress')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-2.5 rounded-sm bg-positive/20 border border-positive/50" /> {t('Done')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-0.5 bg-primary" /> {t('Actual')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3 bg-attention" /> {cursorLabel}
        </span>
      </div>

      <div ref={scrollRef} className="overflow-x-auto overscroll-x-contain -mx-4 sm:mx-0">
      <div className="flex min-w-[900px] sm:min-w-0">
        {/* Phase names + planned dates (stays put while the chart scrolls on a phone) */}
        <div data-name-col className="flex-shrink-0 w-[170px] sm:w-[240px] sticky left-0 z-30 bg-card pl-4 sm:pl-0">
          <div style={{ height: 46 }} />
          {timeline.bars.map((bar) => {
            const st = phaseStatus(bar)
            const canManualClose = st === 'active' && (bar.close_votes ?? 0) >= closeVotesManualThreshold
            return (
              <div key={bar.id} className="flex items-center gap-2 pr-3 border-t border-border-subtle/60" style={{ height: ROW_H }}>
                <span className={cn('w-2 h-2 rounded-full flex-shrink-0', DOT_CLASS[st])} />
                <div className="min-w-0 flex-1">
                  <div
                    className={cn('text-xs font-medium truncate', st === 'next' ? 'text-foreground-soft' : 'text-foreground')}
                    title={bar.name}
                  >
                    {bar.name}
                  </div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">
                    {dateRange(bar.planned_start, bar.planned_end)}
                  </div>
                </div>
                {canManualClose ? (
                  <button
                    type="button"
                    disabled={closing.has(bar.id)}
                    onClick={() => onClosePhase(bar.id)}
                    title={t('Claude has been suggesting closing this phase for {n} days', { n: bar.close_votes })}
                    className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-attention/30 bg-attention/10 text-attention hover:bg-attention/20 disabled:opacity-50"
                  >
                    {closing.has(bar.id) ? t('Closing…') : t('Close ({n}d)', { n: bar.close_votes })}
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>

        {/* Chart: fits the card, no sideways scroll */}
        <div className="relative flex-1 min-w-0">
          {/* Axis header */}
          <div className="relative border-b border-border" style={{ height: 46 }}>
            {ticks.map((m, i) =>
              shown.has(i) ? (
                <span
                  key={`l-${m.label}-${i}`}
                  className={cn(
                    'absolute bottom-1.5 pl-1 text-[11px] tabular-nums whitespace-nowrap',
                    m.major ? 'font-semibold text-foreground' : 'text-muted-foreground',
                  )}
                  style={{ left: `${m.pct}%` }}
                >
                  {m.label}
                </span>
              ) : null,
            )}
            {cursorPct !== null ? (
              <span
                className="absolute top-0 -translate-x-1/2 rounded bg-attention px-1.5 py-px text-[10px] font-semibold text-white whitespace-nowrap z-20"
                style={{ left: `${cursorPct}%` }}
              >
                {cursorLabel}
              </span>
            ) : null}
          </div>

          {/* Rows */}
          <div className="relative">
            {gridLines}
            {timeline.bars.map((bar) => {
              const st = phaseStatus(bar)
              const tip =
                `${bar.name} · ${t(STATUS_LABEL[st])}\n${t('Planned:')} ${dateRange(bar.planned_start, bar.planned_end)}` +
                (bar.actual_start
                  ? `\n${t('Actual:')} ${shortDate(bar.actual_start)} – ${bar.actual_end ? shortDate(bar.actual_end) : t('ongoing')}`
                  : '')
              return (
                <div
                  key={bar.id}
                  className="relative border-t border-border-subtle/60 hover:bg-background/60 transition-colors"
                  style={{ height: ROW_H }}
                  title={tip}
                >
                  <div
                    className={cn('absolute rounded-md border', BAR_CLASS[st])}
                    style={(() => { const c = clampBar(bar.left_pct, bar.width_pct); return { left: `${c.left}%`, width: `${c.width}%`, top: 11, height: 18 } })()}
                  />
                  {bar.actual_start ? (
                    <div
                      className={cn('absolute rounded-full', LINE_CLASS[st])}
                      style={(() => { const c = clampBar(bar.actual_left_pct, bar.actual_width_pct); return { left: `${c.left}%`, width: `${c.width}%`, top: 33, height: 3 } })()}
                    />
                  ) : null}
                </div>
              )
            })}
            {cursorPct !== null ? (
              <div
                className="absolute top-0 bottom-0 border-l-2 border-attention z-10 pointer-events-none"
                style={{ left: `${cursorPct}%` }}
              />
            ) : null}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}
